/**
 * **房间（大厅）模型** —— 玩家列表、准备、游戏规则、踢人。**纯状态机，不碰网络。**
 *
 * 这里刻意只做"房间这块数据长什么样、谁能改什么" —— 也就是**协议的内核**。
 * 联机时 `src/host/lobby.ts` 把这里的每个操作广播出去，而不是重新想一遍规则。
 *
 * ## 为什么房间状态和 `GameState` 是两个东西
 *
 * | | 房间（这里） | 对局（`core/`） |
 * | --- | --- | --- |
 * | 什么时候存在 | 进游戏**之前** | 开局之后 |
 * | 谁能改 | **创建者**（游戏规则、踢人） | 房主（出牌、关窗） |
 * | 权威是谁 | 创建者 | **网络最快的人**（见下） |
 *
 * ⚠️ **"创建房间的人"和"对局里的房主"是两个角色，不能混。**
 * 评审的原话：「进入游戏后的 host 仅由房间中网络最快者担任，和谁创建房间无关。」
 * 所以 `isCreator` 只在**大厅**里意味着权限，开局之后
 * `electGameHost()` 会按延迟重新选一个人当权威 —— 创建者可能落选。
 * 两者唯一的共同点是：都必须是确定性的，所有 peer 独立算出同一个答案。
 *
 * ## 返回值为什么是 `RoomResult` 而不是抛异常
 *
 * 和 `core/reducer.ts` 同一个理由：这些操作将来都会由**远端**触发
 * （"一个已经被踢掉的人还想改游戏规则"、"两个人同时把牌堆改成 0"），
 * 非法输入不该把房间搞崩。返回 `{ok:false, reason}` 让 UI 能直接把原因显示出来。
 */

import { packHash, type CardRegistry, type DeckCounts } from '../core/pack';
import type { PackId } from '../core/ids';
import type { ConfigPatch, PlayerCount, RoomConfig } from '../core/config';
import { MAX_COPIES_PER_TITLE, MAX_PLAYERS, MIN_PLAYERS, officialConfig } from '../core/config';
import { validateConfig } from '../core/setup';
import { nextU32, type RngState } from '../core/rng';
import { t } from '../i18n';

export type RoomResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly reason: string };

export const ok = <T>(value: T): RoomResult<T> => ({ ok: true, value });
export const fail = <T>(reason: string): RoomResult<T> => ({ ok: false, reason });

/** 房间里的座位数 = 人数（钳到官方 2–5） */
function seatCountOf(players: readonly LobbyPlayer[]): PlayerCount {
  return Math.max(MIN_PLAYERS, Math.min(MAX_PLAYERS, players.length)) as PlayerCount;
}

/**
 * 人数变了就把 `config.playerCount` 跟着改 —— 它现在**只由房间里有几个人决定**，
 * 不再是一个手调项（用户要求：去掉"玩家数"设置）。
 */
function syncSeats(state: LobbyState): LobbyState {
  const n = seatCountOf(state.players);
  if (state.config.playerCount === n) return state;
  return { ...state, config: { ...state.config, playerCount: n } };
}

// ============================================================
// 房间号
// ============================================================

/**
 * 房间号长度。
 *
 * 4 位是"能用嘴念出来"和"不容易撞"之间的平衡：31 个字符取 4 位 ≈ 92 万种，
 * 而 5 位在电话里报一遍就开始出错了。
 */
export const ROOM_CODE_LENGTH = 4;

/**
 * 房间号用的字母表。
 *
 * ⚠️ **剔掉了 `I` `L` `O` `0` `1`** —— 它们在手写和口述时必然混淆。
 * 房间号是被"念给朋友听"的东西，为这个场景省掉一类错误是值得的。
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/** 房间号是不是合法（长度对 + 只含字母表内的字符） */
export function isValidRoomCode(code: string): boolean {
  if (code.length !== ROOM_CODE_LENGTH) return false;
  for (const ch of code) {
    if (!ROOM_CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}

/**
 * 把玩家输入的洗成规范形式：转大写、丢掉字母表外的字符、截断。
 *
 * ⚠️ **整条邀请链接粘进来也要能用。** 分享链接长这样
 * `https://host/sub#ABCD`，而"从头取前 4 个合法字符"会把它吃成 `HTTP`
 * —— `https` 里的 H/T/T/P 恰好全在字母表里。所以先摘 `#` 之后那一段
 * （fragment 才是房间号），没有 `#` 才从头洗。
 */
export function normalizeRoomCode(input: string): string {
  // 用最后一个 `#`：链接里至多一个 fragment，最后一个才是房间号那一段
  const hash = input.lastIndexOf('#');
  // 像链接却没有 fragment（没有 #）→ 没有房间号可摘，别把 `https` 洗成 `HTTP`
  if (hash < 0 && input.includes('://')) return '';
  const source = hash >= 0 ? input.slice(hash + 1) : input;
  let out = '';
  for (const ch of source.toUpperCase()) {
    if (!ROOM_CODE_ALPHABET.includes(ch)) continue;
    if (out.length >= ROOM_CODE_LENGTH) break;
    out += ch;
  }
  return out;
}

/**
 * **从 URL hash 里读房间号。** #ABCD → ABCD；不合法（空 / 长度不对 /
 * 含字母表外的字符）返回 null。
 *
 * 用它做"分享链接点开自动进房"：启动时看一眼 location.hash，
 * 有合法房间号就直接走进房那条路。
 */
export function roomCodeFromHash(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const code = raw.trim().toUpperCase();
  return isValidRoomCode(code) ? code : null;
}

/**
 * **去掉 hash 的当前地址**（query 原样保留）。
 *
 * 两个地方要用：
 *
 * - 复制邀请链接时，先把页面自带的旧房间号 hash 剥掉，再拼上新的；
 * - 自动进房**读完之后**抹掉 hash，避免玩家刷新（或把本页再分享出去）
 *   时又触发一次加入。
 */
export function urlWithoutHash(href: string): string {
  const cut = href.indexOf('#');
  return cut < 0 ? href : href.slice(0, cut);
}

/**
 * **能自动进入这个房间的链接。**
 *
 * ⚠️ **不写死任何域名** —— 从**当前页面地址**（location.href，含子路径 /
 * query）现场拼：当前地址（去 hash） + #CODE。这样部署在哪个域名、挂在哪个
 * 子路径下都对。
 *
 * hash 由 roomCodeFromHash 读回。
 */
export function roomJoinUrl(href: string, code: string): string {
  return urlWithoutHash(href) + '#' + code;
}

/** 生成一个房间号。`seed` 由调用方给，所以可复现（测试友好） */
export function generateRoomCode(seed: number): string {
  // 复用 `core/rng` 的 splitmix32 而不是 `Math.random`：
  // 房间号是个会被"念给朋友听"的东西，出问题时能拿种子复现同一个号很有用
  let state: RngState = { seed: seed >>> 0 };
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    const step = nextU32(state);
    state = step.state;
    code += ROOM_CODE_ALPHABET[step.value % ROOM_CODE_ALPHABET.length] ?? 'A';
  }
  return code;
}

// ============================================================
// 大厅状态
// ============================================================

/** 一个玩家**本机装了哪个包、内容指纹是什么**。大厅用它做一致性校验 */
export interface PackRef {
  readonly id: PackId;
  readonly hash: string;
}

export interface LobbyPlayer {
  /** 席位 id。大厅阶段由创建者分配，进游戏后就是 `PlayerId` */
  readonly id: string;
  readonly nickname: string;
  /** 是不是**创建房间**的人。只在大厅里有意义 —— 对局里的房主看 `electGameHost()` */
  readonly isCreator: boolean;
  /** 准备好了没。创建者不需要准备 */
  readonly ready: boolean;
  /**
   * 到房间的往返延迟（毫秒）。
   * `null` = 还没测出来（刚进房）。它决定谁当对局房主，所以是**必须**采的数据。
   */
  readonly pingMs: number | null;
  /** 这位玩家本机装了哪些内容包（id + 指纹）。空 = 还没上报 */
  readonly packs: readonly PackRef[];
  /** 机器人席位：没有对应 peer，由**房主代打**。创建者添加 */
  readonly isBot?: boolean;
}

export interface LobbyState {
  readonly code: string;
  /** 本机玩家的席位 id —— "我"在这些人里的哪一个 */
  readonly selfId: string;
  readonly players: readonly LobbyPlayer[];
  readonly config: RoomConfig;
  /**
   * 已经导入的存档标签（文件名）。
   *
   * ⚠️ 这一轮**存档格式还没定**（评审搁置），所以这里只记"导入了什么"，
   * 不记内容。等格式定下来，这个字段会换成 `SaveFile | null`。
   */
  readonly importedSave: string | null;
}

export function findPlayer(state: LobbyState, id: string): LobbyPlayer | undefined {
  return state.players.find((player) => player.id === id);
}

/** 本机玩家是不是创建者（= 现在能不能改游戏规则 / 踢人） */
export function isSelfCreator(state: LobbyState): boolean {
  return findPlayer(state, state.selfId)?.isCreator === true;
}

/** 创建者：大厅里权限的持有者。**和 `electGameHost()` 不是一回事** */
export function creatorId(state: LobbyState): string | null {
  return state.players.find((player) => player.isCreator)?.id ?? null;
}

/** 一个注册表对应的"本机包列表"（id + 内容指纹） */
export function packRefsOf(registry: CardRegistry): readonly PackRef[] {
  return registry.packs.map((pack) => ({ id: pack.id, hash: packHash(pack) }));
}

export interface CreateLobbyOptions {
  readonly code: string;
  readonly selfId: string;
  readonly nickname: string;
  readonly registry: CardRegistry;
  readonly playerCount?: PlayerCount;
  /** 本机装了哪些包（id + 指纹）。空 = 还没上报 */
  readonly packs?: readonly PackRef[];
}

/** 开一个房间：自己是创建者，配置从官方预设起步 */
export function createLobby(options: CreateLobbyOptions): LobbyState {
  const playerCount = options.playerCount ?? 2;
  return {
    code: options.code,
    selfId: options.selfId,
    players: [
      {
        id: options.selfId,
        nickname: options.nickname,
        isCreator: true,
        ready: true,
        // ⚠️ `null` = **还没测出来**，不是 0。写 0 等于假装本机延迟为零，
        //    而"选网络最快的人当房主"里那一票会让创建者永远赢 ——
        //    恰好把评审那条「和谁创建房间无关」抹掉。延迟要等真的测出来再填
        pingMs: null,
        // 没显式给就按本机注册表算 —— 注册表就是"这台机器装了哪些包"
        packs: options.packs ?? packRefsOf(options.registry),
      },
    ],
    config: officialConfig(playerCount, options.registry),
    importedSave: null,
  };
}

/**
 * **创建者：加一个机器人席位。** 没有对应 peer，进对局后由房主代打。
 *
 * 自动 ready、`packs` 用创建者本机那份（否则「所有人加载内容一致」会挡开局）。
 */
export function addBot(state: LobbyState, registry: CardRegistry): RoomResult<LobbyState> {
  if (!isSelfCreator(state)) return fail(t('room.error.addBotCreatorOnly'));
  if (state.players.length >= MAX_PLAYERS) return fail(t('room.error.seatsFull'));
  let index = 1;
  while (state.players.some((player) => player.id === `bot-${String(index)}`)) index += 1;
  const id = `bot-${String(index)}`;
  return ok(syncSeats({
    ...state,
    players: [
      ...state.players,
      {
        id,
        nickname: `BOT-${String(index)}`,
        isCreator: false,
        ready: true,
        pingMs: null,
        packs: packRefsOf(registry),
        isBot: true,
      },
    ],
  }));
}

/** **把远端创建者加的机器人并进本地模型**（幂等；`packs` 用本地那份，满足一致性校验） */
export function applyRemoteBot(
  state: LobbyState,
  bot: { readonly id: string; readonly nickname: string },
  packs: readonly PackRef[],
): LobbyState {
  if (findPlayer(state, bot.id) !== undefined) return state;
  return syncSeats({
    ...state,
    players: [
      ...state.players,
      {
        id: bot.id,
        nickname: bot.nickname,
        isCreator: false,
        ready: true,
        pingMs: null,
        packs,
        isBot: true,
      },
    ],
  });
}

/** 加入一个已存在的房间。`selfId` 已存在时不重复加人（重连语义） */
export function joinLobby(
  state: LobbyState,
  player: { readonly id: string; readonly nickname: string; readonly packs?: readonly PackRef[] },
): LobbyState {
  const packs = player.packs ?? [];
  if (findPlayer(state, player.id) !== undefined) {
    return syncSeats({
      ...state,
      players: state.players.map((existing) =>
        existing.id === player.id ? { ...existing, nickname: player.nickname, packs } : existing,
      ),
    });
  }
  return syncSeats({
    ...state,
    players: [
      ...state.players,
      { id: player.id, nickname: player.nickname, isCreator: false, ready: false, pingMs: null, packs },
    ],
  });
}

/** 某人离开。创建者走了就把创建者交给**剩下的第一个人**（大厅阶段的兜底） */
export function leaveLobby(state: LobbyState, id: string): LobbyState {
  const players = state.players.filter((player) => player.id !== id);
  if (players.length === state.players.length) return state;
  const hasCreator = players.some((player) => player.isCreator);
  const patched =
    hasCreator || players.length === 0
      ? players
      : players.map((player, index) => (index === 0 ? { ...player, isCreator: true, ready: true } : player));
  return syncSeats({ ...state, players: patched });
}

/**
 * **把远端的玩家状态并进本地大厅模型。**
 *
 * 和 `joinLobby` 的区别：那个是"我要进来"（本机发起），这个是"某人说他
 * 现在是这样"（远端来的）。所以它连 `ready` / `isCreator` 一起认 ——
 * 那两个字段只有本人（或创建者）说了算。
 *
 * ⚠️ `isCreator` 从远端认下来是有意的：创建者是**谁先开的房**，
 * 那件事只有他自己知道，而它决定"谁能改游戏规则"。认下来之后，
 * 真正的权限判定仍然在 `patchConfig` / `kickPlayer` 里做
 * （它们不看这个字段，只看 `state.selfId` 那一条是不是 creator）。
 */
export function applyRemotePlayer(
  state: LobbyState,
  player: {
    readonly id: string;
    readonly nickname: string;
    readonly ready: boolean;
    readonly isCreator: boolean;
    readonly packs?: readonly PackRef[];
  },
): LobbyState {
  const existing = findPlayer(state, player.id);
  if (existing === undefined) {
    return syncSeats({
      ...state,
      players: [
        ...state.players,
        {
          id: player.id,
          nickname: player.nickname,
          // 创建者只有一个：远端说他是，而本地已经有一个了，就不认
          isCreator: player.isCreator && creatorId(state) === null,
          ready: player.ready,
          pingMs: null,
          packs: player.packs ?? [],
        },
      ],
    });
  }
  return syncSeats({
    ...state,
    players: state.players.map((existing_) =>
      existing_.id === player.id
        ? {
            ...existing_,
            nickname: player.nickname,
            ready: player.ready,
            isCreator: existing_.isCreator || (player.isCreator && creatorId(state) === null),
            packs: player.packs ?? existing_.packs,
          }
        : existing_,
    ),
  });
}

/**
 * **认下创建者广播的游戏规则。**
 *
 * 游戏规则只有一个权威 —— 创建房间的人。别人收到就照抄，不做合并、
 * 也不做校验（校验在"开始对局"那一刻统一做一次，
 * 因为改到一半的配置一定自相矛盾，见 `patchConfig` 的说明）。
 */
export function applyRemoteConfig(state: LobbyState, config: RoomConfig): LobbyState {
  // 创建者那份 `playerCount` 可能过时 —— 座位数永远以本机房间人数为准
  return syncSeats({ ...state, config });
}

export function setPlayerReady(state: LobbyState, id: string, ready: boolean): LobbyState {
  const player = findPlayer(state, id);
  // 创建者永远是"就绪"的：他不需要等自己
  if (player === undefined || player.isCreator) return state;
  return {
    ...state,
    players: state.players.map((existing) =>
      existing.id === id ? { ...existing, ready } : existing,
    ),
  };
}

export function setPlayerPing(state: LobbyState, id: string, pingMs: number | null): LobbyState {
  const player = findPlayer(state, id);
  if (player === undefined || player.pingMs === pingMs) return state;
  return {
    ...state,
    players: state.players.map((existing) =>
      existing.id === id ? { ...existing, pingMs } : existing,
    ),
  };
}

/** 踢人。只有创建者能踢，且踢不动自己 */
export function kickPlayer(state: LobbyState, targetId: string): RoomResult<LobbyState> {
  if (!isSelfCreator(state)) return fail(t('room.error.kickCreatorOnly'));
  if (targetId === state.selfId) return fail(t('room.error.kickSelf'));
  if (findPlayer(state, targetId) === undefined) return fail(t('room.error.playerGone'));
  return ok(leaveLobby(state, targetId));
}

// ============================================================
// 对局房主选举 —— ★ 和创建房间的人无关
// ============================================================

/**
 * **选对局房主：房间里网络最快的那个人。**
 *
 * 评审：「进入游戏后的 host 仅由房间中网络最快者担任，和谁创建房间无关。」
 *
 * 三条性质，缺一不可（`architecture.md` §6.4 要求选举必须是纯函数）：
 *
 * 1. **不看 `isCreator`** —— 创建者可能就是那个网络最差的人
 * 2. **延迟未知（`null`）算最差** —— 一个还没测出延迟的人不该被选成权威
 * 3. **平局按 id 字典序** —— 必须让所有 peer 独立算出**同一个**答案，
 *    否则会出现两个人都认为自己是房主
 *
 * 没有玩家时返回 `null`。
 */
export function electGameHost(players: readonly LobbyPlayer[]): string | null {
  let best: LobbyPlayer | null = null;
  for (const player of players) {
    // ★ 机器人不能当对局房主（没有 peer、不会应答 RPC）
    if (player.isBot === true) continue;
    if (best === null) {
      best = player;
      continue;
    }
    const a = player.pingMs ?? Number.POSITIVE_INFINITY;
    const b = best.pingMs ?? Number.POSITIVE_INFINITY;
    if (a < b) {
      best = player;
    } else if (a === b && player.id < best.id) {
      best = player;
    }
  }
  return best?.id ?? null;
}

// ============================================================
// 游戏规则
// ============================================================

// `ConfigPatch` 现在住在 `core/config.ts`：注册表在功能模块里也要用它，
// 定义在界面层会让 core/ 反向依赖 ui/。这里转出去给既有调用方。
export type { ConfigPatch } from '../core/config';

/**
 * 改游戏规则。**只有创建者能改。**
 *
 * ⚠️ 这里**不做合法性校验**（比如"致命牌不能比玩家数还少"）——
 * 那是 `validateConfig()` 的活，而且**必须允许改到一半**：
 * 房主把玩家数从 5 改成 3 的过程中，配置一定会短暂地自相矛盾。
 * 校验放在"开始游戏"那一刻，不放在每次改的时候。
 */
export function patchConfig(state: LobbyState, patch: ConfigPatch): RoomResult<LobbyState> {
  if (!isSelfCreator(state)) return fail(t('room.error.configCreatorOnly'));
  const { combos, playerCount: _playerCount, ...rest } = patch;
  const config: RoomConfig = {
    ...state.config,
    ...rest,
    // 座位数不允许手调 —— 永远跟房间人数走
    playerCount: seatCountOf(state.players),
    combos: { ...state.config.combos, ...(combos ?? {}) },
  };
  return ok({ ...state, config });
}

/** 改某一种牌放几张。0 = 这张牌不进牌堆 */
export function setDeckCount(
  state: LobbyState,
  cardId: string,
  count: number,
): RoomResult<LobbyState> {
  if (!isSelfCreator(state)) return fail(t('room.error.deckCreatorOnly'));
  if (!Number.isFinite(count)) return fail(t('room.error.countNumber'));
  const clamped = Math.max(0, Math.min(MAX_COPIES_PER_TITLE, Math.round(count)));
  const deck: DeckCounts = { ...state.config.deck, [cardId]: clamped };
  return ok({ ...state, config: { ...state.config, deck } });
}

/** 牌堆总张数。UI 要显示它，而且它是配置对不对的第一眼指标 */
export function deckTotal(state: LobbyState): number {
  let sum = 0;
  for (const count of Object.values(state.config.deck)) sum += count;
  return sum;
}

/** 把牌堆恢复成官方预设（只动 `deck`，别的不碰） */
export function resetDeck(state: LobbyState, registry: CardRegistry): RoomResult<LobbyState> {
  if (!isSelfCreator(state)) return fail(t('room.error.deckCreatorOnly'));
  return ok({ ...state, config: { ...state.config, deck: registry.defaultDeck() } });
}

// 官方致命牌数随 `ConfigPatch` 一起下沉到 core/config。
export { officialLethalFor } from '../core/config';

/**
 * **全员都有的内容包**（按 id 取交集，同 id 必须同指纹）。返回 `id → hash`。
 *
 * ⚠️ 缺席 / 还没上报的玩家会让交集变小 —— 也就是说"等大家报上来"是开局前的
 * 正常等待，不是 bug。
 */
export function commonPackRefs(state: LobbyState): ReadonlyMap<PackId, string> {
  const first = state.players[0];
  if (first === undefined) return new Map();
  const out = new Map<PackId, string>();
  for (const ref of first.packs) {
    const universal = state.players.every((player) =>
      player.packs.some((local) => local.id === ref.id && local.hash === ref.hash),
    );
    if (universal) out.set(ref.id, ref.hash);
  }
  return out;
}

/** 房间里**缺**某个包的玩家 id（按包 id 找） */
export function missingForPack(state: LobbyState, packId: PackId): readonly string[] {
  return state.players.filter((player) => !player.packs.some((ref) => ref.id === packId)).map((p) => p.id);
}

/** 本机装了哪些包 */
export function selfPacks(state: LobbyState): readonly PackRef[] {
  return findPlayer(state, state.selfId)?.packs ?? [];
}

/** 一个包在大厅里的**聚合状态**（跨所有玩家） */
export interface PackStatus {
  readonly id: PackId;
  /** 每个有它的玩家的 hash（playerId → hash） */
  readonly hashes: ReadonlyMap<string, string>;
  /** 没有它的玩家 id */
  readonly missing: readonly string[];
  /** 全员都有且 hash 一致（= 房主才能启用） */
  readonly universal: boolean;
  readonly enabled: boolean;
  /** 不能启用的原因；`universal` 时为 `null` */
  readonly reason: string | null;
}

/**
 * **汇总"所有出现过"的内容包**，并给出每个包能不能启用的原因。
 *
 * 顺序：先按 `config.enabledPacks`（注册表顺序），再把其余的按 id 排上去 ——
 * 确定性，且房主看到的是自己那份顺序。
 *
 * 三种"不能启用"：
 * 1. 部分人有、部分人没有 → `缺：X、Y`
 * 2. 全员都有但同 id 的 hash 不同 → `版本不一致`
 * 3. 谁都没有（配置里列着但没人装）→ `没有人装了这个包`
 */
export function packStatuses(state: LobbyState): readonly PackStatus[] {
  const seen = new Set<PackId>();
  const ids: PackId[] = [];
  for (const id of state.config.enabledPacks) {
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  const extra: PackId[] = [];
  for (const player of state.players) {
    for (const ref of player.packs) {
      if (seen.has(ref.id)) continue;
      seen.add(ref.id);
      extra.push(ref.id);
    }
  }
  extra.sort();
  ids.push(...extra);

  return ids.map((id) => {
    const hashes = new Map<string, string>();
    const missing: string[] = [];
    for (const player of state.players) {
      const ref = player.packs.find((candidate) => candidate.id === id);
      if (ref === undefined) missing.push(player.id);
      else hashes.set(player.id, ref.hash);
    }
    let reason: string | null = null;
    if (missing.length > 0) {
      const names = missing
        .map((pid) => findPlayer(state, pid)?.nickname ?? pid)
        .join(t('room.listSeparator'));
      reason = t('room.pack.missing', {
        names: names === '' ? t('room.pack.notReported') : names,
      });
    } else if (new Set(hashes.values()).size > 1) {
      reason = t('room.pack.versionMismatch');
    } else if (hashes.size === 0) {
      reason = t('room.pack.nobodyHas');
    }
    return {
      id,
      hashes,
      missing,
      universal: reason === null,
      enabled: state.config.enabledPacks.includes(id),
      reason,
    };
  });
}

// ============================================================
// 开局前置条件
// ============================================================

export interface StartCheck {
  readonly ok: boolean;
  /** 不能开局的原因（`ok` 时是空数组） */
  readonly problems: readonly string[];
}

/**
 * 现在能不能开局。
 *
 * 三件事一起查：**人数够不够**、**大家准备好没有**、**游戏规则自不自洽**。
 * 游戏规则那部分直接交给 `validateConfig()` —— 规则层已经有权威实现了，
 * 界面层绝不能再写一份"我自己觉得这样行"的判断。
 */
export function canStart(state: LobbyState, registry: CardRegistry): StartCheck {
  const problems: string[] = [];

  if (state.players.length < MIN_PLAYERS) {
    problems.push(t('room.problem.minPlayers', { n: MIN_PLAYERS }));
  }
  if (state.players.length > MAX_PLAYERS) {
    problems.push(t('room.problem.maxPlayers', { n: MAX_PLAYERS }));
  }
  const notReady = state.players.filter((player) => !player.isCreator && !player.ready);
  if (notReady.length > 0) {
    problems.push(
      t('room.problem.notReady', {
        names: notReady.map((player) => player.nickname).join(t('room.listSeparator')),
      }),
    );
  }

  // ★ 内容包一致性：只能启用**全员都有且指纹一致**的包（房主要在交集里选）
  for (const status of packStatuses(state)) {
    if (!status.enabled || status.universal) continue;
    problems.push(
      t('room.problem.packDisabled', {
        id: status.id,
        reason: status.reason ?? t('room.problem.inconsistent'),
      }),
    );
  }

  const validation = validateConfig(state.config, registry);
  // ⚠️ `ValidationResult` 是判别联合：`problems` 只在 `ok: false` 那一支上
  if (!validation.ok) {
    for (const problem of validation.problems) problems.push(problem.message);
  }

  return { ok: problems.length === 0, problems };
}
